import React, { useState } from 'react';
import { FiltersBarStyles } from './FiltersBar.styled.js';
import ChevronLftSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import ChevronRightSvg from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import { useScrollFilterBar } from './useScrollFilterBar';
import FilterBedsBaths from './FilterBedsBaths';
import FilterPrice from './FilterPrice';
import FilterFeatures from './FilterFeatures';
import FilterSize from './FilterSize';
import FilterName from './FilterName';
import { pluralize } from '@utils/pluralize';
import {
  allPriceChoicesMax,
  allPriceChoicesMin,
  DEFAULT_BATHS,
  DEFAULT_BEDS,
  DEFAULT_MAX_PRICE,
  DEFAULT_MAX_SQ_FT,
  DEFAULT_MIN_PRICE,
  DEFAULT_MIN_SQ_FT,
  DEFAULT_SECTION,
  Section,
} from '@utils/homeSearchConstants';
import FindAHomeLocationIndicator from '../FindAHomeLocationIndicator.js';
import { useMobile } from '@hooks/Shared/useMobile';
import { FilterParams } from 'types/FindAHome/HomeSearchProps.js';

type Props = {
  isMoveInReady: boolean;
  baths: number;
  beds: number;
  minPrice: number;
  maxPrice: number;
  maxSquareFeet: number;
  minSquareFeet: number;
  section: Section;
  selectedFeatures: string[];
  locationFilterShowing: boolean;
  setLocationFilterShowing: (show: boolean) => void;
  onPostalCodeChange: () => void;
  onApply: (params: FilterParams) => void;
};

const FiltersBar = ({
  isMoveInReady,
  beds,
  baths,
  minPrice,
  maxPrice,
  minSquareFeet,
  maxSquareFeet,
  section,
  selectedFeatures,
  locationFilterShowing,
  onPostalCodeChange,
  setLocationFilterShowing,
  onApply,
}: Props) => {
  const [priceFilterShowing, setPriceFilterShowing] = useState(false);
  const [bedsBathsFilterShowing, setBedsBathsFilterShowing] = useState(false);
  const [featuresFilterShowing, setFeaturesFilterShowing] = useState(false);
  const [sizeFilterShowing, setSizeFilterShowing] = useState(false);
  const [nameFilterShowing, setNameFilterShowing] = useState(false);
  const { isMobile } = useMobile();

  const {
    touchStart,
    touchEnd,
    touchFilterBar,
    showLeftScroll,
    showRightScroll,
    filterRef,
  } = useScrollFilterBar({ isMobile });

  const closeAllOtherFilters = () => {
    setPriceFilterShowing(false);
    setBedsBathsFilterShowing(false);
    setFeaturesFilterShowing(false);
    setSizeFilterShowing(false);
    setNameFilterShowing(false);
    setLocationFilterShowing(false);
  };

  const isMenuOpen =
    priceFilterShowing ||
    bedsBathsFilterShowing ||
    featuresFilterShowing ||
    sizeFilterShowing ||
    nameFilterShowing ||
    locationFilterShowing;

  let priceText = 'Any Price';
  const priceChanged =
    minPrice !== DEFAULT_MIN_PRICE || maxPrice !== DEFAULT_MAX_PRICE;
  if (priceChanged) {
    if (!isMobile) {
      const min = allPriceChoicesMin.find(({ value }) => value === minPrice)!;
      const max = allPriceChoicesMax.find(({ value }) => value === maxPrice)!;
      priceText = `${min.text} - ${max.text}`;
    } else {
      priceText = 'Price';
    }
  }

  let bedsBathText = 'Any Beds/Baths';
  const bedBathsChanged = beds !== DEFAULT_BEDS || baths !== DEFAULT_BATHS;
  if (bedBathsChanged) {
    if (!isMobile) {
      bedsBathText = `${beds} ${pluralize(beds, 'Bed')} + ${baths} ${pluralize(baths, 'Bath')}`;
    } else {
      bedsBathText = 'Beds/Baths';
    }
  }

  let sizeChanged = false;
  if (
    minSquareFeet !== DEFAULT_MIN_SQ_FT ||
    maxSquareFeet !== DEFAULT_MAX_SQ_FT ||
    section !== DEFAULT_SECTION
  ) {
    sizeChanged = true;
  }

  let featuresText = 'Features';
  const featuresChanged = selectedFeatures.length > 0;
  if (!isMobile && featuresChanged) {
    featuresText = `Features (${selectedFeatures.length})`;
  }

  return (
    <FiltersBarStyles
      $isMenuOpen={isMenuOpen}
      ref={filterRef}
      onTouchMove={touchFilterBar}
    >
      <div
        className="find-a-home-location-indicator-desktop"
        onKeyUp={(ev) => {
          if (ev.key === 'Enter') {
            if (!locationFilterShowing) {
              setLocationFilterShowing(true);
            }
          }
        }}
        onPointerDown={() => {
          if (!locationFilterShowing) {
            setLocationFilterShowing(true);
          }
        }}
      >
        {/* this needs to be in a different flexbox element as filter buttons to be shown properly on desktop */}
        <FindAHomeLocationIndicator
          platform="Desktop"
          showLocationSelector={locationFilterShowing}
          onPostalCodeChange={() => onPostalCodeChange()}
          onPointerDown={() => {
            closeAllOtherFilters();
            setLocationFilterShowing(!locationFilterShowing);
          }}
          onCloseLocationIndicatorClick={() => setLocationFilterShowing(false)}
        />
      </div>

      <div
        className="filters"
        onContextMenu={(ev) => {
          if (isMobile) {
            ev.preventDefault();
          }
        }}
      >
        <div
          className="left-scroll"
          onTouchStart={() => touchStart({ direction: 'left' })}
          onTouchEnd={() => touchEnd()}
        >
          {showLeftScroll ? <ChevronLftSvg /> : null}
        </div>

        {/* this needs to be in the same flexbox element as filterbuttons to be shown properly on mobile */}
        <div
          className="find-a-home-location-indicator-mobile filter"
          onKeyUp={(ev) => {
            if (ev.key === 'Enter') {
              if (!locationFilterShowing) {
                setLocationFilterShowing(true);
              }
            }
          }}
        >
          <FindAHomeLocationIndicator
            platform="Mobile"
            showLocationSelector={locationFilterShowing}
            onPointerDown={() => {
              closeAllOtherFilters();
              setLocationFilterShowing(!locationFilterShowing);
            }}
            onPostalCodeChange={() => onPostalCodeChange()}
            onCloseLocationIndicatorClick={() => {
              setLocationFilterShowing(false);
            }}
          />
        </div>

        <div className="filter-move-in-ready filter">
          <button
            className={
              'filter-button standard' + (isMoveInReady ? ' changed' : '')
            }
            onClick={() => {
              closeAllOtherFilters();
              onApply({ isMoveInReady: !isMoveInReady });
            }}
          >
            Move-in Ready
          </button>
        </div>

        <div className="filter-price filter">
          <button
            className={
              'filter-button standard' +
              (priceChanged ? ' changed' : '') +
              (priceFilterShowing ? ' active' : '')
            }
            onPointerDown={() => {
              closeAllOtherFilters();
              setPriceFilterShowing(!priceFilterShowing);
            }}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                closeAllOtherFilters();
                setPriceFilterShowing(!priceFilterShowing);
              }
            }}
          >
            {priceText}
          </button>
          {priceFilterShowing && (
            <FilterPrice
              minPrice={minPrice}
              maxPrice={maxPrice}
              onCloseClick={() => setPriceFilterShowing(false)}
              onApplyClick={({ minPrice, maxPrice }) => {
                onApply({ minPrice, maxPrice });
                setPriceFilterShowing(false);
              }}
            />
          )}
        </div>

        <div className="filter-beds-baths filter">
          <button
            className={
              'filter-button standard' +
              (bedBathsChanged ? ' changed' : '') +
              (bedsBathsFilterShowing ? ' active' : '')
            }
            onPointerDown={() => {
              closeAllOtherFilters();
              setBedsBathsFilterShowing(!bedsBathsFilterShowing);
            }}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                closeAllOtherFilters();
                setBedsBathsFilterShowing(!bedsBathsFilterShowing);
              }
            }}
          >
            {bedsBathText}
          </button>

          {bedsBathsFilterShowing && (
            <FilterBedsBaths
              beds={beds}
              baths={baths}
              onCloseClick={() => setBedsBathsFilterShowing(false)}
              onApplyClick={({ beds, baths }) => {
                onApply({ beds, baths });
                setBedsBathsFilterShowing(false);
              }}
            />
          )}
        </div>

        <div className="filter-features filter">
          <button
            className={
              'filter-button standard' +
              (featuresChanged ? ' changed' : '') +
              (featuresFilterShowing ? ' active' : '')
            }
            onPointerDown={() => {
              closeAllOtherFilters();
              setFeaturesFilterShowing(!featuresFilterShowing);
            }}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                closeAllOtherFilters();
                setFeaturesFilterShowing(!featuresFilterShowing);
              }
            }}
          >
            {featuresText}
          </button>

          {featuresFilterShowing && (
            <FilterFeatures
              selectedFeatures={selectedFeatures}
              onCloseClick={() => setFeaturesFilterShowing(false)}
              onApplyClick={(features) => {
                onApply({
                  features: encodeURIComponent(features.join(',')),
                });
                setFeaturesFilterShowing(false);
              }}
            />
          )}
        </div>

        <div className="filter-size filter">
          <button
            className={
              'filter-button standard' +
              (sizeChanged ? ' changed' : '') +
              (sizeFilterShowing ? ' active' : '')
            }
            onPointerDown={() => {
              closeAllOtherFilters();
              setSizeFilterShowing(!sizeFilterShowing);
            }}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                closeAllOtherFilters();
                setSizeFilterShowing(!sizeFilterShowing);
              }
            }}
          >
            Size
          </button>

          {sizeFilterShowing && (
            <FilterSize
              minSquareFeet={minSquareFeet}
              maxSquareFeet={maxSquareFeet}
              section={section}
              onCloseClick={() => setSizeFilterShowing(false)}
              onApplyClick={({ minSquareFeet, maxSquareFeet, section }) => {
                onApply({ minSquareFeet, maxSquareFeet, section });
                setSizeFilterShowing(false);
              }}
            />
          )}
        </div>

        <div className="filter-name filter">
          <button
            className={
              'filter-button standard ' + (nameFilterShowing && 'active')
            }
            onPointerDown={() => {
              closeAllOtherFilters();
              setNameFilterShowing(!nameFilterShowing);
            }}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                closeAllOtherFilters();
                setNameFilterShowing(!nameFilterShowing);
              }
            }}
          >
            Name
          </button>

          {nameFilterShowing && (
            <FilterName
              onCloseClick={() => {
                closeAllOtherFilters();
                setNameFilterShowing(false);
              }}
            />
          )}
        </div>

        <div
          className="right-scroll"
          onTouchStart={() => touchStart({ direction: 'right' })}
          onTouchEnd={() => touchEnd()}
        >
          {showRightScroll ? <ChevronRightSvg /> : null}
        </div>
      </div>
    </FiltersBarStyles>
  );
};

export default FiltersBar;
